/** @module                             Hooks */

import useFetch                         from "../../../../Hooks/useFetch";


import { useTranslation }               from "react-i18next";

/** @module                             React-Icons */

import { AiOutlineCloseCircle }         from "react-icons/ai";

import { ImSpinner5 }                   from "react-icons/im";

/** @module                             Queries */

import { ORDER_PRICE_CALCULATOR }       from "../../../../Queries/Orders";

/** @module                             Components */

import Image                            from "../../Global/Table/Image";

import Text                             from "../../Global/Table/Text";

import { Fragment, useLayoutEffect, useMemo, useRef }          from "react";

import ProductsName from "./Products/ProductsName";

import ProductsVendor from "./Products/ProductsVendor";

import RetailPrice from "./Products/RetailPrice";

import { FaImage } from "react-icons/fa";

/**
 * Same rule as Edit order submit: prefer explicit custom delivery price for totals and calculator input.
 */

function effectiveDeliveryPriceForOrder ( order ) {

    if ( order.deliveryPriceManuallyEdited ) {

        const fromInput = order.deliveryPrice != null && order.deliveryPrice !== ""
            ? parseFloat ( order.deliveryPrice ) : NaN;

        if ( ! Number.isNaN ( fromInput ) ) return fromInput;

        const fromCustom = order.customDeliveryPrice != null && order.customDeliveryPrice !== ""
            ? parseFloat ( order.customDeliveryPrice ) : NaN;

        return Number.isNaN ( fromCustom ) ? 0 : fromCustom;

    }

    if ( order.deliveryPrice != null && order.deliveryPrice !== "" ) {

        const v = parseFloat ( order.deliveryPrice );

        if ( ! Number.isNaN ( v ) ) return v;

    }

    if ( order.customDeliveryPrice != null && order.customDeliveryPrice !== "" ) {

        const v = parseFloat ( order.customDeliveryPrice );

        if ( ! Number.isNaN ( v ) ) return v;

    }

    return 0;

}

/**
 * Payload for `ORDER_PRICE_CALCULATOR` `product_params` (same shape as admin order lines).
 */
export function priceCalculatorProducts ( productsList ) {

    return productsList.map ( product => {

        return { id: product.productId, qty: product.count, price: parseFloat ( product.price ), discount: product.discount };

    } );

}

/**
 * GraphQL variables for `ORDER_PRICE_CALCULATOR` / `OrderPriceCalculatorResolver`.
 *
 * @param { Object } order - Order form state
 * @param { string|number|null|undefined } [orderId] - Persisted order id (edit page); omit on create
 */
export function buildOrderPriceCalculatorVariables ( order, orderId ) {

    const resolvedOrderId = orderId ?? order?.id;

    return {
        countryCode: order.countryCode,
        city: order.city,
        orderId: resolvedOrderId ? String ( resolvedOrderId ) : undefined,
        deliveryPlanId: order.deliveryPlanId || undefined,
        deliveryPrice: effectiveDeliveryPriceForOrder ( order ),
        servicePrice: order.assembly ? order.assemblyPrice : 0,
        vat: order.vatIncluded,
        productParams: priceCalculatorProducts ( order.orderProductVendors || [] )
    };

}

/**
 * Stable key for the same inputs as `OrderPriceCalculatorResolver` (excluding `delivery_price` fallback).
 */
function orderPriceCalculatorInputsFingerprint ( o, orderId ) {

    return JSON.stringify ( {
        orderId: String ( orderId ?? o.id ?? "" ),
        countryCode: o.countryCode || "",
        city: o.city || "",
        deliveryPlanId: o.deliveryPlanId ?? "",
        servicePrice: o.assembly ? Number ( o.assemblyPrice ) : 0,
        vat: !! o.vatIncluded,
        products: priceCalculatorProducts ( o.orderProductVendors || [] )
    } );

}

const 

/**
 * 
 * @param { Object } order 
 * 
 * @returns product table of current order
 * 
 */

OrderProducts = ( { order, setState, isEditing = false, orderId } ) => { const 
    
    { t : __ } = useTranslation (),

    calculatorInputsFingerprintRef = useRef ( null ),

    orderPriceCalculatorVariables = buildOrderPriceCalculatorVariables ( order, orderId ),
    
    removeProduct = ( e, id ) => { e.preventDefault ();

        const orderProductVendors = order.orderProductVendors.filter ( product => product.productId != id );

        setState ( { ...order, orderProductVendors } );

    },

    updateQuantity = ( e, id) => {

        if ( e.target.value === "" ) return;

        if ( parseInt ( e.target.value ) < 0 ) return;

        const orderProductVendors = order.orderProductVendors.map((product) => {

            if (product.productId === id) {

                const totalQty = sumQuantity ( product.product.productVendors );

                const updatedProduct = { ...product, count: ( parseInt ( e.target.value ) > totalQty ) ? totalQty : parseInt ( e.target.value ) };
                
                return updatedProduct;
            }

            return product;

          });
          
          setState({ ...order, orderProductVendors });

    },


    updateProductPrice = ( e, id) => {

        if ( e.target.value === "" ) return;

        let price = parseFloat ( e.target.value );

        if ( price < 1 ) price = 1;

        const orderProductVendors = order.orderProductVendors.map((product) => {

            if (product.productId === id) {

                const updatedProduct = { ...product, price: price, };
                
                return updatedProduct;
            }

            return product;

          });
          
          setState({ ...order, orderProductVendors });

    },

    updateDiscount = ( e, id) => {

        if ( e.target.value === "" ) return;

        let discount = parseFloat ( e.target.value );

        if ( discount < 0 ) discount = 0;

        if ( discount > 100 ) discount = 100;

        const orderProductVendors = order.orderProductVendors.map((product) => {

            if (product.productId === id) {

                const updatedProduct = { ...product, discount: discount};
                
                return updatedProduct;
            }

            return product;

          });
          
          setState({ ...order, orderProductVendors });

    },

    /**
     * Same fields passed to `ORDER_PRICE_CALCULATOR` / OrderPriceCalculatorResolver (excluding delivery_price fallback).
     * When this changes on the edit page, we sync delivery from the resolver again instead of keeping the stale DB value.
     */
    calculatorInputsFingerprint = useMemo (
        () => orderPriceCalculatorInputsFingerprint ( order, orderId ),
        [
            orderId,
            order.id,
            order.countryCode,
            order.city,
            order.deliveryPlanId,
            order.assembly,
            order.assemblyPrice,
            order.vatIncluded,
            order.orderProductVendors
        ]
    ),

    /** 
     * 
     * 
     * @event useFetch 
     * 
     * @returns query data, loading state
     * 
     */

    { loading, data } = useFetch ( ORDER_PRICE_CALCULATOR, orderPriceCalculatorVariables, "cache-and-network", true ),

    calc = data?.orderPriceCalculator,

    calcDelivery = calc?.delivery ?? 0,

    effectiveDelivery = effectiveDeliveryPriceForOrder ( order ),

    deliveryDelta = effectiveDelivery - calcDelivery,

    adjustedTotal = calc ? calc.total + deliveryDelta : 0,

    vatRatio = 21 / ( 100 + 21 ),

    adjustedVat = order.vatIncluded && calc ? adjustedTotal * vatRatio : 0,

    adjustedTotalExeclVat = order.vatIncluded && calc ? adjustedTotal - adjustedVat : adjustedTotal;

    useLayoutEffect ( () => {

        if ( loading || ! calc || ! setState ) return;

        const nextDiscount = calc.totalDiscount ?? 0;
        const nextDelivery = calc.delivery ?? 0;

        const prevFp = calculatorInputsFingerprintRef.current;
        const calculatorInputsChanged = prevFp !== null && prevFp !== calculatorInputsFingerprint;

        setState ( prev => {

            const round = v => Number ( Number ( v ?? 0 ).toFixed ( 2 ) );

            const useCalcDelivery = ! prev.deliveryPriceManuallyEdited;

            /**
             * Edit page: keep persisted delivery on first paint / refetch (same resolver inputs).
             * When inputs that affect OrderPriceCalculatorResolver change, apply its delivery + totals.
             */
            if ( isEditing && useCalcDelivery ) {

                if ( calculatorInputsChanged ) {

                    const targetDelivery = round ( nextDelivery );
                    const targetTotal = round ( calc.total ?? 0 );

                    const next = {
                        price: targetTotal,
                        discountPrice: round ( nextDiscount ),
                        deliveryPrice: targetDelivery,
                        customDeliveryPrice: targetDelivery
                    };

                    const prevSnap = {
                        price: round ( prev.price ),
                        discountPrice: round ( prev.discountPrice ),
                        deliveryPrice: round ( prev.deliveryPrice ),
                        customDeliveryPrice: round ( prev.customDeliveryPrice )
                    };

                    const unchanged = Object.keys ( next ).every ( k => prevSnap[ k ] === next[ k ] );

                    if ( unchanged ) return prev;

                    return { ...prev, ...next };

                }

                const savedDelivery = effectiveDeliveryPriceForOrder ( prev );
                const calcDelivery = round ( nextDelivery );
                const saved = round ( savedDelivery );
                const targetTotal = round ( ( calc.total ?? 0 ) + ( saved - calcDelivery ) );

                const next = {
                    price: targetTotal,
                    discountPrice: round ( nextDiscount )
                };

                const prevSnap = {
                    price: round ( prev.price ),
                    discountPrice: round ( prev.discountPrice )
                };

                const unchanged = Object.keys ( next ).every ( k => prevSnap[ k ] === next[ k ] );

                if ( unchanged ) return prev;

                return { ...prev, ...next };

            }

            const targetDelivery = useCalcDelivery ? nextDelivery : ( prev.deliveryPrice ?? 0 );

            const targetTotal = useCalcDelivery
                ? ( calc.total ?? 0 )
                : adjustedTotal;

            const next = {
                price: round ( targetTotal ),
                discountPrice: round ( nextDiscount ),
                deliveryPrice: round ( targetDelivery ),
                customDeliveryPrice: round ( targetDelivery )
            };

            const prevSnap = {
                price: round ( prev.price ),
                discountPrice: round ( prev.discountPrice ),
                deliveryPrice: round ( prev.deliveryPrice ),
                customDeliveryPrice: round ( prev.customDeliveryPrice )
            };

            const unchanged = Object.keys ( next ).every ( k => prevSnap[ k ] === next[ k ] );

            if ( unchanged ) return prev;

            return useCalcDelivery
                ? { ...prev, ...next }
                : { ...prev, price: next.price, discountPrice: next.discountPrice };

        } );

        calculatorInputsFingerprintRef.current = calculatorInputsFingerprint;

    }, [ loading, calc, adjustedTotal, setState, isEditing, calculatorInputsFingerprint ] );

    /** @returns */

    return ( 
    
        <table>

            <thead>

                <tr>

                    <th>
                                
                        &#160;
                        
                    </th>

                    <th>
                                
                        <FaImage />
                        
                    </th>


                    {/* <th>
                                
                        { __( "id" ) }
                        
                    </th> */}

                    <th>
                                
                        { __( "product" ) }
                        
                    </th>

                    {/* <th>
                                
                        { __( "productType" ) }
                        
                    </th> */}

                    <th>
                                
                        { __( "vendor" ) }
                                                
                    </th>

                    <th>
                                
                        { __( "shopPrice" ) }
                                
                    </th>

                    <th>
                                
                        { __( "price" ) }
                        
                    </th>

                    <th>
                                
                        { __( "discount" ) + " %" }
                                        
                    </th>

                    <th>
                                
                        { __( "quantity" ) }
                                
                    </th>

                    <th>
                                
                        { __( "totals" ) }
                        
                    </th>

                </tr>

            </thead>

            <tbody>

                { ( typeof order != "undefined" ) && 

                    order.orderProductVendors.map ( ( product, index ) => {

                        const

                        productTypes = {

                            tire: `${ product.product.width } | ${ product.product.height } | R${ parseInt ( product.product.diameter ) }${ product.product?.isCTire ? "C" : "" } | ${ product.product?.loadIndex }${ product.product?.speedRating?.toUpperCase () }`,

                            rim: `${ product?.product?.width || "-" }J | R${ parseInt ( product?.product?.diameter ) || "-" } | ${ product?.product?.pitchCircleDiameter || "-" } | ET${ product?.product?.offset || "-" } | CB${ product?.product?.centreBore || "-" }`,

                            battery: `${ product.product.voltage } V | ${ product.product.current } A | ${ product.product.capacity } Ah`,

                        };

                        return (

                            <tr key={ index }>

                                <td className="centered">

                                    <span onClick={ e => removeProduct ( e, product.productId ) } className="standalone_remove_action">

                                        <AiOutlineCloseCircle size={ 20 } />

                                    </span>

                                </td>

                                <Image src={ product.product.currentImage } alt={ product.id } />

                                {/* <Text text={ __ ( product.productId ) } /> */}

                                <ProductsName product={ product.product } />

                                {/* <Link 
                                
                                    text={ `${ product.product.manufacturer.name.toUpperCase () } ${ product.product.model } - ${ productTypes[ product.product.productType ] }` }

                                    route={ `/app/products/edit/${ product.product.id }` }
                            
                                /> */}

                                {/* <Text text={ __ ( product.product.productType ) } /> */}

                                {/* <Text text={ __ ( product.productVendorId ? product.vendorName : product.product.vendorNames.join(", ") ) } /> */}

                                <ProductsVendor product={ product } />
                                
                                {/* <Text text={ `\u20AC ${ parseFloat ( product.originalPrice || product.product.priceList.retail_price ).toFixed ( 2 ) }` } /> */}

                                <RetailPrice product={ product } />

                                {isEditing ?
                                    <>

                                        <td data-title="text" className="input_cell">

                                            <input type="number" style={ { width: 100 } } value={ product.price } onChange={ e => updateProductPrice ( e, product.productId ) } />

                                        </td>

                                        <td data-title="text" className="input_cell">

                                            <input type="number" style={ { width: 100 } } value={ product.discount } onChange={ e => updateDiscount ( e, product.productId ) } />

                                        </td>
                                    </>
                                :
                                    <>
                                
                                        <Text text={ `\u20AC ${ parseFloat ( product.price ).toFixed ( 2 ) }` } />

                                        <Text text="0" />
                                    </>
                                }

                                <td data-title="text" className="input_cell">

                                    <input type="number" style={ { width: 100 } } value={ product.count } onChange={ e => updateQuantity ( e, product.productId ) } />

                                </td>

                                <td data-title="text">

    { ( () => {

        const baseTotal = parseFloat ( product.price * product.count );

        const discountPct = parseFloat ( product.discount ) || 0;

        const discountedTotal = baseTotal * ( 1 - discountPct / 100 );

        return discountPct > 0 ? (

            <>
                <span style={ { textDecoration: "line-through", opacity: 0.5, marginRight: 6 } }>
                    &euro; { baseTotal.toFixed ( 2 ) }
                </span>
                <span>
                    &euro; { discountedTotal.toFixed ( 2 ) }
                </span>
            </>

        ) : (

            <>&euro; { baseTotal.toFixed ( 2 ) }</>

        );

    } ) () }

</td>
                            </tr>

                        )

                    } )
                
                }

                { order.assembly && 

                    <tr>

                        <td colSpan="7">&nbsp;</td>

                        <td>{ __ ( "assembly" ) }:</td>

                        <td>

                            { loading ? <ImSpinner5 className="spinner" size={ 16 } /> : 

                                <Fragment>
                            
                                    &euro;

                                    &nbsp;

                                    { data?.orderPriceCalculator?.service?.toFixed ( 2 ) || "0.00" }

                                </Fragment>
                            
                            }

                        </td>

                    </tr>

                }

                <tr>

                    <td colSpan="7">&nbsp;</td>

                    <td>{ __ ( "delivery" ) }:</td>

                    <td>

                        { loading ? <ImSpinner5 className="spinner" size={ 16 } /> : 

                            <Fragment>
                        
                                &euro;

                                &nbsp;

                                { effectiveDelivery.toFixed ( 2 ) }

                            </Fragment>
                        
                        }

                    </td>

                </tr>

                <tr>

                    <td colSpan="7">&nbsp;</td>

                    <td>{ __ ( "discount" ) }:</td>

                    <td>

                        { loading ? <ImSpinner5 className="spinner" size={ 16 } /> : 

                            <Fragment>
                        
                                &euro;

                                &nbsp;

                                { "- " + ( data?.orderPriceCalculator?.totalDiscount?.toFixed ( 2 ) || "0.00" ) }

                            </Fragment>
                        
                        }

                    </td>

                </tr>

                <tr>

                    <td colSpan="7">&nbsp;</td>

                    <td>{ __ ( "total_execl_vat" ) }:</td>

                    <td>

                        { loading ? <ImSpinner5 className="spinner" size={ 16 } /> : 

                            <Fragment>
                        
                                &euro;

                                &nbsp;

                                { adjustedTotalExeclVat.toFixed ( 2 ) }

                            </Fragment>
                        
                        }

                    </td>

                </tr>

                <tr>

                    <td colSpan="7">&nbsp;</td>

                    <td>{ __ ( "vat" ) }:</td>

                    <td>

                        { loading ? <ImSpinner5 className="spinner" size={ 16 } /> : 

                            <Fragment>
                        
                                &euro;

                                &nbsp;

                                { 

                                    order?.vatIncluded ? adjustedVat.toFixed ( 2 ) : 0.00
                                
                                }

                            </Fragment>
                        
                        }

                    </td>

                </tr>

                <tr>

                    <td colSpan="7">&nbsp;</td>

                    <td>{ __ ( "order_total" ) }:</td>

                    <td>

                        
                        {

                            loading ? <ImSpinner5 className="spinner" size={ 16 } /> : 

                            <Fragment>

                                &euro;

                                &nbsp;
                            
                                { adjustedTotal.toFixed ( 2 ) }

                            </Fragment>

                        }


                    </td>

                </tr>

                        <tr>

                    <td colSpan="7">&nbsp;</td>

                    <td colSpan="2">

                        <label style={{ display: "flex", alignItems: "center", gap: 6, cursor: "pointer" }}>

                            <input
                                type="checkbox"
                                checked={!!order.hideTotalsInPdf}
                                onChange={e => setState({ ...order, hideTotalsInPdf: e.target.checked })}
                            />

                            {__("hide_totals_in_pdf")}

                        </label>

                    </td>

                </tr>

            </tbody>

        </table> 
        
    );

},

/**
 * 
 * @param { Array } productsList
 * 
 * @returns total quantity of products
 * 
 * @description sums up all quantities of products in order
 * 
 */

sumQuantity = ( productsList ) => {

    return productsList.reduce ( ( acc, cur ) => acc + cur.qty + cur.qtyDays + cur.qtyHour + cur.qtyWeeks, 0 );

}

/** @exports OrderProducts */
 
export default OrderProducts;